import React, { Dispatch, SetStateAction } from "react";

import {
  Card,
  Heading,
  Stack,
  Text,
  Alert,
  Button,
} from "@origin-digital/ods-core";
import { ClaimableJourneyPropertyDetails, ClaimableStates } from "./types";
import { getClaimDisplayAddressString } from "./helper";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";

interface IClaimStateProps {
  address?: ClaimableJourneyPropertyDetails;
  referenceNumber: string;
  claimableState?: ClaimableStates;
  submitForm: () => void;
  ocNumber?: string;
  error: AxiosError | null;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

const ClaimState = ({
  address,
  referenceNumber,
  claimableState,
  submitForm,
  ocNumber,
  error,
  setOpen,
}: IClaimStateProps) => {
  const determineComponentToDisplay = () => {
    switch (claimableState) {
      case ClaimableStates.CLAIMABLE:
        return <ClaimableState handleClaimRequest={submitForm} />;
      case ClaimableStates.CLAIMED:
        return <ClaimedState ocNumber={ocNumber} setOpen={setOpen} />;
      case ClaimableStates.CLAIMED_BY_SOMEONE_ELSE:
        return <ClaimedBySomeoneElseState />;
      case ClaimableStates.NOT_FOUND:
        return <NothingFoundState />;
      case ClaimableStates.COMPLETED:
        return <CompletedBySomeoneElseState />;
      default:
        return <NothingFoundState />;
    }
  };

  return error ? (
    <Alert tone="critical" title="Oops, something went wrong!">
      It's not you, it's us. Please try again or check back later.
    </Alert>
  ) : (
    <Card outline>
      <Stack>
        <Stack space="xxsmall">
          <Heading variant="h4">{referenceNumber}</Heading>
          <Text>
            {address
              ? getClaimDisplayAddressString(address, address?.site_details)
              : ""}
          </Text>
        </Stack>
        {determineComponentToDisplay()}
      </Stack>
    </Card>
  );
};

const ClaimableState = (props: { handleClaimRequest: () => void }) => {
  const { handleClaimRequest } = props;
  return (
    <Stack>
      <Alert tone="info" title="Available to be claimed">
        This ID can be claimed by you or your company
      </Alert>
      <Button variant="outlined" size="small" onClick={handleClaimRequest}>
        Claim request
      </Button>
    </Stack>
  );
};

const ClaimedState = (props: {
  ocNumber?: string;
  setOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  const { ocNumber, setOpen } = props;
  const navigate = useNavigate();
  return (
    <Stack>
      <Alert tone="positive" title="Claimed successfully">
        This ID has been claimed successfully by you or your company.
      </Alert>
      <Button
        variant="outlined"
        size="small"
        onClick={() => {
          setOpen(false);
          navigate(`/new-premise/${ocNumber}`);
        }}
      >
        View request
      </Button>
    </Stack>
  );
};

const ClaimedBySomeoneElseState = () => (
  <Alert tone="caution" title="Already claimed by someone else">
    This is a correct ID but it has been claimed by someone else.
  </Alert>
);

const CompletedBySomeoneElseState = () => (
  <Alert tone="caution" title="Cannot be claimed ">
    The connection has already been completed and can't be claimed anymore.
  </Alert>
);

const NothingFoundState = () => (
  <Alert tone="critical" title="Couldn't find a request with this ID">
    If you're sure the ID is correct, then try again later. Sometimes it takes
    several hours or even days for a distributor to send us the request.
  </Alert>
);

export default ClaimState;
